/**
 * P3-19/20/21: ScienceTab —— Science（Library）tab 组装根（迁移自
 * com.nuclearunicorn.game.ui.tab.Library，js/science.js:2719-2839）。
 *
 * 当前迁移科技按钮列表、政策区与 Metaphysics 区；tab 切换与视觉比对留 P3-21。
 *
 * 结构对齐：
 * - 根 div.tabInner.Science（js/ui.js:448-457 的 tab 内容容器层级）；
 * - 右上角 "Hide researched" 复选框（id=toggleResearched，civil 科技研究后显示）；
 * - table.table（width:100%）> tbody > tr > td，内部垂直堆叠 TechButton。
 */
import { useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";
import { PolicyPanel } from "./PolicyPanel";
import { MetaphysicsPanel } from "./MetaphysicsPanel";
import { TechButton } from "./TechButton";

interface ScienceSnapshot {
  hasCivil: boolean;
  hideResearched: boolean;
  techs: string[];
}

function computeSnapshot(game: GamePage | null): ScienceSnapshot | null {
  if (!game) {
    return null;
  }
  const science = game.science as {
    techs: { name: string }[];
    hideResearched: boolean;
    get?: (name: string) => { researched?: boolean };
  };
  const hasCivil = science.get?.("civil")?.researched ?? false;
  return {
    hasCivil,
    hideResearched: science.hideResearched,
    techs: science.techs.map((tech) => tech.name),
  };
}

function snapshotEq(a: ScienceSnapshot | null, b: ScienceSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.hasCivil === b.hasCivil &&
    a.hideResearched === b.hideResearched &&
    a.techs.length === b.techs.length &&
    a.techs.every((name, i) => name === b.techs[i])
  );
}

export default function ScienceTab() {
  const snap = useGameTick(
    useCallback((game: GamePage | null) => computeSnapshot(game), []),
    snapshotEq,
  );

  const toggleHideResearched = useCallback(() => {
    const game = getGame();
    if (!game) {
      return;
    }
    const science = game.science as { hideResearched: boolean };
    science.hideResearched = !science.hideResearched;
  }, []);

  const content = useMemo(() => {
    if (!snap) {
      return null;
    }
    return snap.techs.map((name) => <TechButton key={name} techName={name} />);
  }, [snap]);

  if (!snap) {
    return null;
  }

  return (
    <TooltipProvider>
      <div className="tabInner Science">
        <div style={{ float: "right" }}>
          <input
            id="toggleResearched"
            type="checkbox"
            checked={snap.hideResearched}
            style={{ display: snap.hasCivil ? "" : "none" }}
            onChange={toggleHideResearched}
          />
          <label htmlFor="toggleResearched">{t("science.toggleResearched.label")}</label>
        </div>
        <table className="table" style={{ width: "100%" }}>
          <tbody>
            <tr>
              <td>{content}</td>
            </tr>
          </tbody>
        </table>
        <PolicyPanel />
        <MetaphysicsPanel />
      </div>
    </TooltipProvider>
  );
}
